import { toast } from "react-toastify";
import {
  Paper,
  Typography,
  Stack,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import dayjs from "dayjs";
import {
  Dangerous,
  Error,
  LocalFireDepartment,
  ReportProblem,
} from "@mui/icons-material";
import { useMemo } from "react";
import alertSoundFile from "../../../assets/sounds/new-notification.mp3";

const alertSound = new Audio(alertSoundFile);
alertSound.volume = 0.5;

const getAlertType = (severity, theme, upMd = false) => {
  const SIZE_ICON = upMd ? "1.5dvw" : "24px";
  if (severity == 2)
    return {
      cor: theme.palette.color.zabbixSeverty.warning,
      icon: (
        <ReportProblem
          sx={{
            color: theme.palette.color.zabbixSeverty.warning,
            fontSize: SIZE_ICON,
            zIndex: 2,
          }}
        />
      ),
      title: "Alerta",
    };

  if (severity == 3)
    return {
      cor: theme.palette.color.zabbixSeverty.average,
      icon: (
        <Error
          sx={{
            color: theme.palette.color.zabbixSeverty.average,
            fontSize: SIZE_ICON,
            zIndex: 2,
          }}
        />
      ),
      title: "Alerta Moderado",
    };

  if (severity == 4)
    return {
      cor: theme.palette.color.zabbixSeverty.high,
      icon: (
        <Dangerous
          sx={{
            color: theme.palette.color.zabbixSeverty.high,
            fontSize: SIZE_ICON,
            zIndex: 2,
          }}
        />
      ),
      title: "Alerta Alto Impacto",
    };

  return {
    cor: theme.palette.color.zabbixSeverty.disaster,
    icon: (
      <LocalFireDepartment
        sx={{
          color: theme.palette.color.zabbixSeverty.disaster,
          fontSize: SIZE_ICON,
          zIndex: 2,
        }}
      />
    ),
    title: "Alerta Desastre",
  };
};

function CardAlertToast({ alert, onClick }) {
  const theme = useTheme();
  const upMd = useMediaQuery((theme) => theme.breakpoints.up("lg"));

  const typeCard = useMemo(() => {
    return getAlertType(alert?.severity, theme, upMd);
  }, [alert?.severity, theme, upMd]);
  const styles = {
    card: {
      border: `1px solid ${theme.palette.color.appBarBorder}`,
      position: "relative",
      width: "17dvw",
      p: 1,
      minWidth: { xs: "250px", md: "320px" },
      minHeight: "10px",
      height: "fit-content",
      fontSize: { xs: "0.75rem", md: "0.85rem", lg: "0.8dvw" },
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
      mb: 0.5,
      backgroundColor: theme.palette.background.paper,
    },

    line: {
      position: "absolute",
      left: 0,
      top: 0,
      height: "100%",
      width: "4px",
      backgroundColor: typeCard.cor,
    },

    title: {
      fontSize: "0.9em",
      fontWeight: 600,
      width: "100%",
    },

    date: {
      fontSize: "0.75em",
      color: theme.palette.text.secondary,
      mt: "-2px",
    },

    element: {
      fontSize: "0.9em",
      fontWeight: 500,
      mt: 0.5,
    },

    description: {
      fontSize: "0.8em",
      fontWeight: 500,
      maxWidth: "95%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      color: theme.palette.text.secondary,
    },
    pulse: {
      position: "absolute",
      zIndex: 0,
      left: "50%",
      top: "50%",
      transform: "translateX(-50%) translateY(-50%)",
      width: { xs: "13px", md: "18px" },
      height: { xs: "13px", md: "18px" },
      "&:before": {
        content: '""',
        position: "relative",
        display: "block",
        width: "300%",
        height: "300%",
        boxSizing: "border-box",
        marginLeft: "-100%",
        marginTop: "-100%",
        borderRadius: "50%",
        backgroundColor: typeCard.cor,
        animation: "pulse-ring 2s cubic-bezier(0.215, 0.61, 0.355, 1) infinite",
      },

      "@keyframes pulse-ring": {
        "0%": {
          transform: "scale(.33)",
        },
        "80%, 100%": {
          opacity: 0,
        },
      },
      "@keyframes pulse-dot": {
        "0%": {
          transform: "scale(.8)",
        },
        "50%": {
          transform: "scale(1)",
        },
        "100%": {
          transform: "scale(.8)",
        },
      },
    },
  };

  return (
    <Paper onClick={onClick} sx={styles.card}>
      {/* <Box sx={styles.line} /> */}

      <Stack
        sx={{
          width: "17%",
          position: "relative",
          justifyContent: "center",
          ml: -1,
          alignItems: "center",
        }}
      >
        {typeCard.icon}
        <Box sx={styles.pulse} />
      </Stack>

      <Stack sx={{ width: "83%" }}>
        <Typography sx={styles.title}>
          {"Novo " + typeCard.title + " Detectado!"}
        </Typography>

        <Typography sx={styles.date}>
          {dayjs(alert?.lastchange * 1000).format("DD/MM/YYYY HH:mm")}
        </Typography>

        <Typography sx={styles.element}>
          {alert?.element?.titulo || "Elemento Desconhecido"}
        </Typography>

        <Typography sx={styles.description}>{alert?.description}</Typography>
      </Stack>
    </Paper>
  );
}

const ToastAlert = ({ alert, focus, element, theme, sound = false }) => {
  //verifica se o toast já está sendo exibido para o mesmo alerta
  if (toast.isActive(`${alert.triggerid}-${alert.eventid}`)) return;
  const typeCard = getAlertType(alert?.severity, theme);
  if (sound) {
    alertSound.currentTime = 0;
    alertSound.play().catch(() => {});
  }
  toast(
    <CardAlertToast
      alert={{ ...alert, element }}
      onClick={() => {
        focus(alert.elementId);
      }}
    />,
    {
      style: {
        padding: 0,
        marginTop: "5px",
        "--toastify-color-progress-dark": typeCard.cor,
        "--toastify-toast-width": "fit-content",
        "--toastify-toast-height": "fit-content",
      },
      toastId: `${alert.triggerid}-${alert.eventid}`,
      position: "top-right",
      autoClose: 30000,
      pauseOnHover: true,
      theme: "dark",
    },
  );
};

export default ToastAlert;
